import { capitalize } from '@ember/string';
import { isEmpty } from '@ember/utils';
import ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';

// The API's Grade enum uses UNSPECIFIED for "no rule set". Treat null and
// undefined the same way, since a Subject saved before these fields existed
// may carry neither.
export function isGradeSet(grade) {
  return !isEmpty(grade) && grade !== ProgressionJudgement.UNSPECIFIED;
}

/**
 * subjectLevels lists all five progress levels for a Subject, lowest first,
 * each named the Subject's own way where it has a name (its success criteria)
 * and the school's (or the built-in) way otherwise.
 *
 * possibleGrades alone is not enough: a Subject that names only some levels
 * leaves the rest out of it, although learners can still be judged at them.
 *
 * @param {Ember.Model<goals/subject>} subject
 * @return {Array<{value: number, string: string}>}
 */
export function subjectLevels(subject) {
  return (subject && subject.get('judgementGrades')) || [];
}

/**
 * levelLabel is a progress judgement's name for a Subject, for display: its
 * success criterion exactly as entered where set, otherwise the school's
 * term, capitalised.
 *
 * @param {Ember.Model<goals/subject>} subject
 * @param {number} judgement
 * @return {string}
 */
export function levelLabel(subject, judgement) {
  let level = subjectLevels(subject).find((entry) => entry.value === judgement);
  if (!level) return '';
  return level.isSubjectName ? level.string : capitalize(level.string || '');
}

/**
 * defaultOnTrackGrades is the rule a Subject counts against before anyone has
 * set one: the third of its progress levels for interim Milestones and the
 * fourth for full ones.
 *
 * @param {Ember.Model<goals/subject>} subject
 * @return {{interim: number, full: number}}
 */
export function defaultOnTrackGrades(subject) {
  let levels = subjectLevels(subject);
  let at = (index, fallback) =>
    levels[index] ? levels[index].value : fallback;
  return {
    interim: at(2, ProgressionJudgement.AT),
    full: at(3, ProgressionJudgement.ABOVE),
  };
}

/**
 * onTrackGrades is the judgement a learner must reach to count as on track,
 * per Milestone type. An unset interim grade alongside a set full grade means
 * the full grade covers both, which is how the Subject stores "one rule".
 * Anything left unset falls back to the defaults above.
 *
 * @param {Ember.Model<goals/subject>} subject
 * @return {{interim: number, full: number}}
 */
export default function onTrackGrades(subject) {
  let interim = subject.get('onTrackInterimGrade');
  let full = subject.get('onTrackFullGrade');
  let defaults = defaultOnTrackGrades(subject);

  if (isGradeSet(full)) {
    return { interim: isGradeSet(interim) ? interim : full, full };
  }
  return {
    interim: isGradeSet(interim) ? interim : defaults.interim,
    full: defaults.full,
  };
}
